import dayjs from "dayjs";
import LocalizedFormat from "dayjs/plugin/localizedFormat.js";
import { convertToDayjs } from "../../../lib/dateHelpers.js";
import { getLastPosition } from "../../../lib/reportUtilities.js";
import { CCSetup } from "../../cclib/CCSetup";
import { CCStyling } from "../../cclib/CCStyling";
import { CCIcon } from "../../cclib/components/UI";
import { CCMultiActive } from "../../cclib/types/multi";
import { TableDataRow } from "../../cclib/types/table";
import { afcsDataPrefix } from "../afcs/afcs";
import { reputationColumns } from "../afcs/tables";
dayjs.extend(LocalizedFormat);

type ContribKeys =
  `contrib_${"clinical" | "teaching" | "admin" | "schol" | "other"}`;
type ContribValues = Record<ContribKeys | `${ContribKeys}_change`, string>;

const CONTRIB_METRICS = [
  "contrib_clinical",
  "contrib_teaching",
  "contrib_admin",
  "contrib_schol",
  "contrib_other"
] as const;
export function getContributionValues(cc: CCSetup): ContribValues {
  const data = cc?.newDef?.career?.[cc.viewYear]?.data ?? {};
  return CONTRIB_METRICS.reduce((acc, curr) => {
    return {
      ...acc,
      [curr]: data[cc.dataPrefix + curr] ?? "",
      [curr + "_change"]: data[cc.dataPrefix + curr + "_change"] ?? ""
    };
  }, {} as ContribValues);
}

/**
 * Clean up some things that are hard to due with pure CCStructureItems.
 * Expects an HTML element to exist with id `contrib-container` and children `.confInteger[data-key*="contrib"]`
 */
export function cleanUpContributionSection(cc: CCSetup) {
  const contribContainer = $(`#contrib-container`);
  const contribInputs = contribContainer.find(
    `.confInteger[data-key*="contrib"]`
  );
  const contribInputParents = contribInputs.parent();
  contribInputParents.removeAttr("style");
  contribInputParents.append("%");
  const inputStyle = CCStyling.fromObject({
    width: "80px",
    "margin-right": "5px",
    "text-align": "center"
  });
  contribContainer.append(
    `<div style="display: flex; flex-direction: row; justify-content: space-between;">` +
      `<span data-key="_unmapped_error_binding_left" style="border-top: 2px solid #ccc;padding-top:5px;"><span id="contrib-left" disabled class="form-control" style="${inputStyle.compile()}; display: inline-block;">${0}</span>%</span>` +
      `<span id="copy-contrib" style="margin-top: 7px;" class="btn btn-default"><span class="glyphicon glyphicon-copy" style="margin-right: 5px;"></span><span>Copy current to anticipated</span></span>` +
      `<span data-key="_unmapped_error_binding_right" style="border-top: 2px solid #ccc;padding-top:5px;"><span id="contrib-right" disabled class="form-control" style="${inputStyle.compile()}; display: inline-block;">${0}</span>%</span>` +
      `</div>`
  );
}

function validContribValue(v: string): [number, boolean] {
  const parsed = Number.parseFloat(v);
  return [
    parsed,
    /[^0-9%.]+/g.exec(v) === null &&
      !Number.isNaN(parsed) &&
      Number.isFinite(parsed)
  ];
}
/**
 * Update the displayed total values for each contribution column.
 * Expects HTML elements with ids `contrib-left` and `contrib-right` to exist.
 * @param [errorRight=false] - Should the right column give the user an error when it does not equal 100%?
 */
export function updateContributionTotals(cc: CCSetup) {
  const vals = getContributionValues(cc);
  const totals = [0, 0];
  for (const [key, value] of Object.entries(vals)) {
    const [parsed, valid] = validContribValue(value);
    let arrIndex = key.endsWith("_change") ? 1 : 0;
    if (valid) {
      totals[arrIndex] += parsed;
    }
  }
  $("#contrib-left").text(totals[0]);
  $("#contrib-right").text(totals[1]);
  cc.forceErrorState("#contrib-left", totals[0] != 100);
  cc.forceErrorState("#contrib-right", totals[1] != 100);
  return totals;
}

/**
 * Listen for input events on contribution inputs
 * Expects an HTML element to exist with id `contrib-container` and children `.confInteger[data-key*="contrib"]`
 */
export function watchContributionInputs(cc: CCSetup) {
  const contribContainer = $(`#contrib-container`);
  const contribInputs = contribContainer.find(
    `.confInteger[data-key*="contrib"]`
  );
  contribInputs.on("input", () => {
    updateContributionTotals(cc);
  });
  $("#copy-contrib")
    .off("click")
    .on("click", () => {
      for (const metric of CONTRIB_METRICS) {
        const data = cc.newDef.career?.[cc.viewYear]?.data ?? {};
        const entry = data[cc.dataPrefix + metric];
        if (entry !== undefined) {
          data[cc.dataPrefix + metric + "_change"] = entry;
        }
      }
      cc.update(cc.struct.structure.filter((i) => i.type === "grid"));
    });
}

export function copyLastYearContribValues(cc: CCSetup) {
  const lastYear = cc.newDef.career?.[cc.viewYear - 1]?.data ?? {};

  for (const metric of CONTRIB_METRICS) {
    const previousData = lastYear[cc.dataPrefix + metric + "_change"];
    if (
      typeof previousData === "number" &&
      !Number.isNaN(previousData) &&
      Number.isFinite(previousData)
    ) {
      cc.setValueIfUndefined(metric, previousData);
    }
    cc.setValueIfPredicate(metric, 0, (v) => !validContribValue(String(v))[1]);
    cc.setValueIfPredicate(
      metric + "_change",
      0,
      (v) => !validContribValue(String(v))[1]
    );
  }
}
/**
 * Corresponds to the exported `deptEMForm` structure in `shared/structure.ts`.
 */
export function setupDeptEMForm(cc: CCSetup, transformLine: "UML/UTL" | "CE") {
  const stanfordLineMap = cc.contextConstants["stanfordLineMap"] ?? {};
  let stanfordLine = stanfordLineMap[cc.newDef.narrative?.stanfordLine] ?? "";
  if (stanfordLine.toLowerCase() === transformLine.toLowerCase()) {
    stanfordLine =
      transformLine === "CE" ? "Clinician Educator" : "University Medical Line";
  }
  const positions = getLastPosition(cc.newDef, { returnAll: true }) ?? [];
  const lastPosition = positions[positions.length - 1] ?? {};

  cc.populateReadonlyValues([
    {
      key: "faculty_name",
      value: cc.cvOwner.name
    },
    {
      key: "faculty_line",
      value: stanfordLine
    },
    {
      key: "faculty_rank",
      value: lastPosition.title
    },
    {
      key: "current_appointment_end_date",
      value: dayjs(convertToDayjs(lastPosition?.time?.end)).format("YYYY-MM-DD")
    },
    {
      key: "annual_counselor",
      value: cc.contextConstants.users?.[cc.mentorList?.[0]]?.name ?? ""
    }
  ]);
  const data = cc.newDef.career?.[cc.viewYear]?.data;
  cc.setValueIfUndefined("actionable_goal_1", data[afcsDataPrefix + "goal_1"]);
  cc.setValueIfUndefined("actionable_goal_2", data[afcsDataPrefix + "goal_2"]);
  cc.setValueIfUndefined("actionable_goal_3", data[afcsDataPrefix + "goal_3"]);
  copyLastYearContribValues(cc);
  // add <hr>
  const HR_MARGIN_PX = 40;
  const HR_BORDER_PX = 2;
  $("#part2-boundary").before(
    `<hr style="margin: ${HR_MARGIN_PX}px 0px ${HR_MARGIN_PX}px 0px; border-top: ${HR_BORDER_PX}px solid black;"/>`
  );
}

export function outputContribution(cc: CCSetup): Record<string, string> {
  const data = cc.newDef.career[cc.viewYear].data;
  const contribKeys = ["clinical", "teaching", "admin", "schol", "other"];
  return Object.fromEntries(
    contribKeys
      .map((key) => [
        [`contrib_${key}`, data?.[cc.dataPrefix + `contrib_${key}`] ?? ""],
        [
          `contrib_${key}_change`,
          data?.[cc.dataPrefix + `contrib_${key}_change`] ?? ""
        ]
      ])
      .flat(1)
  );
}

export function addDownloadInternalAFCSButton(
  cc: CCSetup,
  active: CCMultiActive
) {
  const className =
    active.shortTitle === "clinical"
      ? ".downloadCeAnnualCounseling"
      : active.shortTitle === "senior"
        ? ".downloadSeniorAnnualCounseling"
        : ".downloadAssistantAnnualCounseling";

  cc.addButton(
    "primary",
    CCIcon("download") + "<span>Internal AFCS</span>",
    {
      relative: "after",
      selector: className
    },
    "downloadInternalAfcs",
    () => {
      const data = cc.newDef.career[cc.viewYear].data;
      const dataOverride = {};
      if (active.shortTitle !== "senior") {
        dataOverride["reputation_section"] = true;
        dataOverride["type_of_review_section"] = true;
        dataOverride["promotion_questions_section"] = true;
        const typeOfReview = data[active.dataPrefix + "type_of_review"];
        if (typeOfReview === "Reappointment") {
          dataOverride["review_type_reappointment"] = true;
        } else if (
          typeOfReview ===
          "Promotion Review will be initiated approximately six months in advance of the current appointment end date"
        ) {
          dataOverride["review_type_promotion"] = true;
        } else if (
          typeOfReview === "Type of review has not yet been determined"
        ) {
          dataOverride["review_type_undetermined"] = true;
        }
        const reputation = data[afcsDataPrefix + "custom-reputation"] ?? {};
        for (const repItem of Object.values(reputation).filter(Boolean)) {
          for (const [key, value] of Object.entries(repItem)) {
            if (key != "_number") {
              dataOverride[`reputation_${repItem["_number"]}_${key}`] = value;
            }
          }
        }
      } else {
        // 4 = number of reputation items expected
        for (let i = 1; i <= 4; ++i) {
          for (const key of [
            "email",
            "first_name",
            "institution",
            "last_name"
          ]) {
            dataOverride[`reputation_${i}_${key}`] = "N/A";
          }
        }
      }
      cc.conferenceExport(
        "stanford_em_internal_afcs.docx",
        `Internal_AFCS_${cc.cvOwner.name}_${dayjs(new Date()).format("MM-DD-YYYY")}`,
        dataOverride,
        () => ""
      );
    }
  );
}

export function cleanUpCheckboxes() {
  const containers = $(
    "input.form-control.confCheck[type='checkbox']"
  ).parent();
  containers.css({
    display: "flex",
    "flex-direction": "row",
    width: "min(1020px, 100%)",
    "padding-left": "10px",
    "align-items": "center"
  });
  const labels = containers.children("h5");
  labels.on("click", function () {
    $(this).siblings().trigger("click");
  });
  labels.each((_, element) => {
    if (element.innerText.endsWith(":")) {
      element.innerText = element.innerText.substring(
        0,
        element.innerText.length - 1
      );
    }
  });
  labels.css("width", "100%");
  const inputs = containers.children("input");
  inputs.css("margin", "0 10px 0 0");
}

export function getReputationData(
  data: Record<string, unknown>,
  dataPrefix: string
): Partial<TableDataRow<typeof reputationColumns>> & {
  include_reputation: boolean;
} {
  const res: Partial<TableDataRow<typeof reputationColumns>> = {};
  const reputation = data[dataPrefix + "custom-reputation"] ?? {};
  for (const repItem of Object.values(reputation).filter(Boolean)) {
    for (const [key, value] of Object.entries(repItem)) {
      if (key != "_number") {
        res[`reputation_${repItem["_number"]}_${key}`] = value;
      }
    }
  }
  return {
    ...res,
    include_reputation: $("[data-reputation-hidden]").length === 0
  };
}
